import React from 'react';
import clsx from 'clsx';

import {
  useAddVideoCompleteMutation,
  useAddVideoFavoriteMutation,
  useRemoveVideoCompleteMutation,
  useRemoveVideoFavoriteMutation,
  useVideoStatusQuery,
  useViewerSubscriptionQuery,
} from '@edx/graphql/generated';
import { useAuth } from '@edx/hooks/use-auth';
import { useNotification } from '@edx/hooks/use-notification';
import { IconCheckCircle, IconHeartCircle } from '@edx/components/icons';
import { Caption } from '@edx/components/typography';

type Props = {
  courseSlug: string;
  videoId: string;
  videoSlug: string;
};

export const VideoActions: React.FC<Props> = ({ courseSlug, videoSlug, videoId }) => {
  const { isLoggedIn } = useAuth();
  const viewerSubscriptionQuery = useViewerSubscriptionQuery();
  const { data } = useVideoStatusQuery({ variables: { courseSlug, slug: videoSlug }, fetchPolicy: 'network-only' });
  const [onAddVideoComplete] = useAddVideoCompleteMutation();
  const [onRemoveVideoComplete] = useRemoveVideoCompleteMutation();
  const [onAddVideoFavorite] = useAddVideoFavoriteMutation();
  const [onRemoveVideoFavorite] = useRemoveVideoFavoriteMutation();
  const { showNotification } = useNotification();
  const { showNotification: showErrorNotification } = useNotification('error');

  const userIsSubscribed =
    !viewerSubscriptionQuery.loading &&
    !viewerSubscriptionQuery.error &&
    viewerSubscriptionQuery.data?.viewer.subscription?.isValid;
  const isActionEnabled = (isLoggedIn && data?.video?.isPublic) || userIsSubscribed;
  const isCompleted = isActionEnabled && Boolean(data?.video?.viewerHasCompleted);
  const isFavorited = isActionEnabled && Boolean(data?.video?.viewerHasFavorited);

  const handleToggleCompleted = async (): Promise<void> => {
    if (!isActionEnabled) return;

    const onToggle = isCompleted ? onRemoveVideoComplete : onAddVideoComplete;
    const mutationType = isCompleted ? 'removeVideoComplete' : 'addVideoComplete';
    onToggle({
      variables: { input: { videoId } },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      optimisticResponse: {
        __typename: 'Mutation',
        [mutationType]: {
          __typename: 'Video',
          id: videoId,
          viewerHasCompleted: !isCompleted,
        },
      },
    })
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .then((response) => {
        showNotification({
          message: response.data[mutationType].viewerHasCompleted
            ? 'Marcado como completado.'
            : 'Eliminado de tu lista de completados.',
        });
      })
      .catch(() => {
        showErrorNotification({ title: '¡Ups!', message: 'No se ha podido marcar como completado.' });
      });
  };

  const handleToggleFavorited = async (): Promise<void> => {
    if (!isLoggedIn) return;

    const onToggle = isFavorited ? onRemoveVideoFavorite : onAddVideoFavorite;
    const mutationType = isFavorited ? 'removeVideoFavorite' : 'addVideoFavorite';
    await onToggle({
      variables: { input: { videoId } },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      optimisticResponse: {
        __typename: 'Mutation',
        [mutationType]: {
          __typename: 'Video',
          id: videoId,
          viewerHasFavorited: !isFavorited,
        },
      },
    }) // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .then((response) => {
        showNotification({
          message: response.data[mutationType].viewerHasFavorited
            ? 'Guardado en tu lista de favoritos.'
            : 'Eliminado de tu lista de favoritos.',
        });
      })
      .catch(() => {
        showErrorNotification({ title: '¡Ups!', message: 'No se ha podido guardar en tu lista de favoritos.' });
      });
  };

  return (
    <div className="flex justify-between items-center text-gray-500 h-20 border-t md:border-t-0 border-l-0 md:border-l border-r-0 md:border-r border-gray-600 border-opacity-50">
      <div className="flex-grow p-4">
        <Caption tag="span" size="small" variant="semibold">
          Marcar como:
        </Caption>
      </div>

      <div className="h-full flex border-opacity-50 divide-x divide-gray-600 divide-opacity-50">
        <div className="flex w-24 h-full items-center justify-center">
          <button
            onClick={handleToggleCompleted}
            disabled={!isActionEnabled}
            className={clsx('w-12 h-12 flex items-center justify-center focus:outline-none', {
              'opacity-40': !isActionEnabled,
            })}
            aria-label={isCompleted ? 'Eliminar de tu lista de completados' : 'Marcar como completado'}
            title={isCompleted ? 'Eliminar de tu lista de completados' : 'Marcar como completado'}
            aria-pressed={isCompleted}
          >
            <IconCheckCircle className={clsx('w-7 h-7', isCompleted ? 'text-green' : 'text-white')} />
          </button>
        </div>

        <div className="flex w-24 h-full items-center justify-center">
          <button
            onClick={handleToggleFavorited}
            disabled={!isActionEnabled}
            className={clsx('w-12 h-12 flex items-center justify-center focus:outline-none', {
              'opacity-40': !isActionEnabled,
            })}
            aria-label={isFavorited ? 'Eliminar de tu lista de favoritos' : 'Marcar como favorito'}
            title={isFavorited ? 'Eliminar de tu lista de favoritos' : 'Marcar como favorito'}
            aria-pressed={isFavorited}
          >
            <IconHeartCircle className={clsx('w-7 h-7', isFavorited ? 'text-primary-500' : 'text-white')} />
          </button>
        </div>
      </div>
    </div>
  );
};
